import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query
} from "firebase/firestore";
import { ref as storageRef, getDownloadURL } from "firebase/storage";
import { httpsCallable } from "firebase/functions";
import {
  ArrowDownTrayIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  TrashIcon
} from "@heroicons/react/24/outline";
import { db, storage, functions } from "../../firebase/config";
import PdfViewer from "../../components/PdfViewer";

const SIGNER_STATUS = {
  pending: { label: "Not started", color: "text-slate-500" },
  otp_verified: { label: "Viewing", color: "text-amber-600" },
  signed: { label: "Signed", color: "text-emerald-600" },
  declined: { label: "Declined", color: "text-red-600" }
};

function SignerTimeline({ signer }) {
  const ms = (ts) => ts?.toMillis?.() ?? ts?.toDate?.()?.getTime?.() ?? Infinity;
  const events = [];

  if (Array.isArray(signer.inviteLog) && signer.inviteLog.length > 0) {
    for (const entry of signer.inviteLog) {
      if (entry.via === "copy") {
        events.push({ label: "Link copied manually", time: entry.sentAt, state: "done" });
      } else {
        events.push({ label: "Invite sent", time: entry.sentAt, state: "done" });
        if (entry.status === "delivered")
          events.push({ label: "Email delivered", time: entry.statusAt, state: "done" });
        else if (entry.status === "bounced")
          events.push({ label: "Email bounced", time: entry.statusAt, state: "error" });
        else if (entry.status === "complained")
          events.push({ label: "Spam complaint", time: entry.statusAt, state: "warn" });
        if (entry.openedAt)
          events.push({ label: "Email read", time: entry.openedAt, state: "done" });
      }
    }
  } else if (signer.inviteEmailStatus) {
    // Backward compat: old records that used flat fields.
    events.push({ label: "Invite sent", time: signer.inviteEmailSentAt, state: "done" });
    if (signer.inviteEmailStatus === "delivered")
      events.push({ label: "Email delivered", time: signer.inviteEmailStatusAt, state: "done" });
    else if (signer.inviteEmailStatus === "bounced")
      events.push({ label: "Email bounced", time: signer.inviteEmailStatusAt, state: "error" });
    else if (signer.inviteEmailStatus === "complained")
      events.push({ label: "Spam complaint", time: signer.inviteEmailStatusAt, state: "warn" });
  }

  if (signer.otpVerifiedAt) {
    const label = signer.verificationMode === "email_only" ? "Link opened" : "Identity verified";
    events.push({ label, time: signer.otpVerifiedAt, state: "done" });
  }
  if (signer.status === "signed" && signer.signedAt)
    events.push({ label: "Signed", time: signer.signedAt, state: "done" });
  else if (signer.status === "declined")
    events.push({ label: "Declined", time: null, state: "error" });

  // Sort latest first; nulls (no timestamp) sort to the top.
  events.sort((a, b) => ms(a.time) - ms(b.time)).reverse();

  if (events.length === 0)
    return <p className="py-2 text-sm text-slate-400">No activity yet.</p>;

  const dot = (state) =>
    state === "done" ? "bg-emerald-500" : state === "error" ? "bg-red-500" : "bg-orange-400";

  return (
    <ol className="relative ml-2 border-l border-slate-200 py-1">
      {events.map((e, i) => (
        <li key={i} className="mb-3 ml-4 last:mb-0">
          <span className={`absolute -left-1.5 mt-1 h-3 w-3 rounded-full border-2 border-white ${dot(e.state)}`} />
          <div className="flex items-baseline justify-between gap-4">
            <span className="text-sm text-slate-700">{e.label}</span>
            {e.time && <span className="shrink-0 text-xs text-slate-400">{fmtDateTime(e.time)}</span>}
          </div>
        </li>
      ))}
    </ol>
  );
}

const fmtDateTime = (ts) => {
  const d = ts?.toDate?.();
  if (!d) return "";
  return d.toLocaleString("en-NZ", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
};

export default function DocumentDetail() {
  const { docId } = useParams();
  const navigate = useNavigate();
  const [docData, setDocData] = useState(null);
  const [signers, setSigners] = useState([]);
  const [finalUrl, setFinalUrl] = useState(null);
  const [originalUrl, setOriginalUrl] = useState(null);
  const [currentStateUrl, setCurrentStateUrl] = useState(null);
  const [buildingPreview, setBuildingPreview] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [resending, setResending] = useState({});
  const [resendResult, setResendResult] = useState({});
  const [copying, setCopying] = useState({});
  const [copyResult, setCopyResult] = useState({});
  const [expandedSigners, setExpandedSigners] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);
  const intentionallyDeleting = useRef(false);

  useEffect(() => {
    const swallowOnDelete = (err) => {
      if (intentionallyDeleting.current) return;
      console.error(err);
    };
    const unsubDoc = onSnapshot(
      doc(db, "documents", docId),
      (snap) => {
        setDocData(snap.exists() ? { id: snap.id, ...snap.data() } : null);
      },
      swallowOnDelete
    );
    const signersQ = query(
      collection(db, "documents", docId, "signers"),
      orderBy("order", "asc")
    );
    const unsubSigners = onSnapshot(
      signersQ,
      (snap) =>
        setSigners(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      swallowOnDelete
    );
    return () => {
      unsubDoc();
      unsubSigners();
    };
  }, [docId]);

  useEffect(() => {
    if (docData?.status === "complete" && docData?.finalPdfPath) {
      getDownloadURL(storageRef(storage, docData.finalPdfPath)).then(setFinalUrl);
    }
  }, [docData?.status, docData?.finalPdfPath]);

  useEffect(() => {
    if (docData?.originalPdfPath) {
      getDownloadURL(storageRef(storage, docData.originalPdfPath))
        .then(setOriginalUrl)
        .catch(() => setOriginalUrl(null));
    }
  }, [docData?.originalPdfPath]);

  const previewUrl = finalUrl || currentStateUrl || originalUrl;
  const previewLabel = finalUrl
    ? "signed PDF"
    : currentStateUrl
    ? "current state (partial signatures embedded)"
    : "original PDF";

  const buildCurrentState = async () => {
    setBuildingPreview(true);
    try {
      const fn = httpsCallable(functions, "previewDocument");
      const { data } = await fn({ docId });
      setCurrentStateUrl(data.url);
      setShowPreview(true);
    } catch (err) {
      alert(err?.message || "Could not build preview");
    } finally {
      setBuildingPreview(false);
    }
  };

  const resend = async (signerId) => {
    setResending((r) => ({ ...r, [signerId]: true }));
    setResendResult((r) => ({ ...r, [signerId]: null }));
    try {
      const fn = httpsCallable(functions, "resendSigningInvite");
      await fn({ docId, signerId });
      setResendResult((r) => ({
        ...r,
        [signerId]: { type: "success", message: "Invite re-sent" }
      }));
    } catch (err) {
      setResendResult((r) => ({
        ...r,
        [signerId]: {
          type: "error",
          message: err?.message || "Could not resend"
        }
      }));
    } finally {
      setResending((r) => ({ ...r, [signerId]: false }));
      setTimeout(() => {
        setResendResult((r) => {
          const copy = { ...r };
          delete copy[signerId];
          return copy;
        });
      }, 6000);
    }
  };

  const copyLink = async (signerId) => {
    setCopying((c) => ({ ...c, [signerId]: true }));
    setCopyResult((c) => ({ ...c, [signerId]: null }));
    try {
      const fn = httpsCallable(functions, "copySigningLink");
      const { data } = await fn({ docId, signerId });
      await navigator.clipboard.writeText(data.url);
      setCopyResult((c) => ({ ...c, [signerId]: { type: "success", message: "Copied!" } }));
    } catch (err) {
      setCopyResult((c) => ({
        ...c,
        [signerId]: { type: "error", message: err?.message || "Could not copy" }
      }));
    } finally {
      setCopying((c) => ({ ...c, [signerId]: false }));
      setTimeout(() => {
        setCopyResult((c) => {
          const copy = { ...c };
          delete copy[signerId];
          return copy;
        });
      }, 4000);
    }
  };

  const onDelete = async () => {
    if (
      !window.confirm(
        `Delete "${docData.title}"? This removes the document, signatures, and audit trail. This cannot be undone.`
      )
    )
      return;
    setDeleting(true);
    intentionallyDeleting.current = true;
    try {
      const fn = httpsCallable(functions, "deleteDocument");
      await fn({ docId });
      navigate("/admin");
    } catch (err) {
      intentionallyDeleting.current = false;
      alert(err?.message || "Delete failed");
      setDeleting(false);
    }
  };

  if (!docData) return <div className="p-6 text-slate-500">Loading…</div>;

  return (
    <div className="mx-auto max-w-3xl p-4">
      <Link to="/admin" className="text-sm text-brand-600">
        ← Back to documents
      </Link>
      <div className="mt-2 flex items-start justify-between gap-3">
        <h1 className="text-2xl font-semibold">{docData.title}</h1>
        <button
          onClick={onDelete}
          disabled={deleting}
          className="btn-secondary text-sm text-red-600 hover:bg-red-50"
          aria-label="Delete document"
        >
          <TrashIcon className="mr-1 inline h-4 w-4" />
          {deleting ? "Deleting…" : "Delete"}
        </button>
      </div>
      <div className="mb-4 text-sm text-slate-500">
        Status: <strong>{docData.status}</strong> · Mode: {docData.mode}
        {docData.createdAt && <> · created {fmtDateTime(docData.createdAt)}</>}
        {docData.sentAt && <> · sent {fmtDateTime(docData.sentAt)}</>}
        {docData.completedAt && <> · completed {fmtDateTime(docData.completedAt)}</>}
      </div>
      {docData.status === "send_failed" && (
        <div className="mb-4 rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-800">
          Email send failed: {docData.lastSendError}. Delete the document and try
          again once the sender is configured.
        </div>
      )}

      <section className="card mb-4">
        <h2 className="mb-2 font-semibold">Signers</h2>
        <ul className="divide-y divide-slate-100">
          {signers.map((s) => {
            const st = SIGNER_STATUS[s.status] || SIGNER_STATUS.pending;
            const expanded = expandedSigners.has(s.id);
            const toggle = () =>
              setExpandedSigners((prev) => {
                const next = new Set(prev);
                next.has(s.id) ? next.delete(s.id) : next.add(s.id);
                return next;
              });
            return (
              <li key={s.id}>
                <button
                  className="flex w-full items-center justify-between py-2 text-left"
                  onClick={toggle}
                >
                  <div>
                    <div className="font-medium">{s.name}</div>
                    <div className="text-xs text-slate-500">
                      {s.email}{s.phoneE164 ? ` · ${s.phoneE164}` : ""}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2 pl-3">
                    <span className={`text-sm ${st.color}`}>{st.label}</span>
                    {expanded
                      ? <ChevronUpIcon className="h-4 w-4 text-slate-400" />
                      : <ChevronDownIcon className="h-4 w-4 text-slate-400" />}
                  </div>
                </button>

                {expanded && (
                  <div className="pb-3 pl-1 pr-1">
                    <SignerTimeline signer={s} />
                    {s.status !== "signed" && (
                      <div className="mt-3 space-y-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <button
                            className="btn-secondary text-xs"
                            onClick={() => copyLink(s.id)}
                            disabled={copying[s.id]}
                          >
                            {copying[s.id] ? "Copying…" : "Copy link"}
                          </button>
                          <button
                            className="btn-secondary text-xs"
                            onClick={() => resend(s.id)}
                            disabled={resending[s.id]}
                          >
                            {resending[s.id] ? "Resending…" : "Resend invite"}
                          </button>
                        </div>
                        {copyResult[s.id] && (
                          <p className={"text-xs " + (copyResult[s.id].type === "success" ? "text-emerald-600" : "text-red-600")}>
                            {copyResult[s.id].message}
                          </p>
                        )}
                        {resendResult[s.id] && (
                          <p className={"text-xs " + (resendResult[s.id].type === "success" ? "text-emerald-600" : "text-red-600")}>
                            {resendResult[s.id].message}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </section>

      <section className="card mb-4">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-semibold">Document</h2>
          <div className="flex flex-wrap items-center gap-2">
            {originalUrl && (
              <a
                className="btn-secondary text-sm"
                href={originalUrl}
                target="_blank"
                rel="noreferrer"
                download
              >
                <ArrowDownTrayIcon className="mr-1 inline h-4 w-4" />
                Original
              </a>
            )}
            {docData.status !== "complete" && (
              <button
                className="btn-secondary text-sm"
                onClick={buildCurrentState}
                disabled={buildingPreview}
              >
                <ArrowDownTrayIcon className="mr-1 inline h-4 w-4" />
                {buildingPreview ? "Building…" : "Current state"}
              </button>
            )}
            {currentStateUrl && docData.status !== "complete" && (
              <a
                className="btn-primary text-sm"
                href={currentStateUrl}
                target="_blank"
                rel="noreferrer"
                download
              >
                Download current state
              </a>
            )}
            {finalUrl && (
              <a
                className="btn-primary text-sm"
                href={finalUrl}
                target="_blank"
                rel="noreferrer"
                download
              >
                <ArrowDownTrayIcon className="mr-1 inline h-4 w-4" />
                Signed PDF
              </a>
            )}
            {previewUrl && (
              <button
                className="btn-secondary text-sm"
                onClick={() => setShowPreview((v) => !v)}
              >
                {showPreview ? "Hide" : "Show"} preview
              </button>
            )}
          </div>
        </div>
        <p className="text-xs text-slate-500">
          Showing {previewLabel}.{" "}
          {docData.status !== "complete" &&
            "Tap Current state to build a working copy with every signature captured so far — useful when a signer needs to print it and finish on paper."}
        </p>
        {showPreview && previewUrl && (
          <div className="mt-3 max-h-[75vh] overflow-y-auto rounded-lg border border-slate-200 p-2">
            <PdfViewer file={previewUrl} />
          </div>
        )}
      </section>

      {docData.status === "complete" && (
        <section className="card space-y-3">
          <h2 className="font-semibold">Integrity</h2>
          <div className="text-sm text-slate-600">
            <div>
              Recorded SHA-256:{" "}
              <code className="break-all text-xs">
                {docData.finalPdfSha256}
              </code>
            </div>
            <p className="mt-1 text-xs text-slate-500">
              This hash was computed from the final PDF bytes at the moment
              of signing and written to the audit log. Verifying recomputes
              the hash from the stored file and compares — any alteration
              produces a different hash.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              className="btn-secondary text-sm"
              disabled={verifying}
              onClick={async () => {
                setVerifying(true);
                setVerifyResult(null);
                try {
                  const fn = httpsCallable(functions, "verifyIntegrity");
                  const { data } = await fn({ docId });
                  setVerifyResult(data);
                } catch (err) {
                  setVerifyResult({
                    ok: false,
                    error: err?.message || "Verification failed"
                  });
                } finally {
                  setVerifying(false);
                }
              }}
            >
              {verifying ? "Verifying…" : "Verify integrity"}
            </button>
            {verifyResult && verifyResult.ok && (
              <span className="text-sm text-emerald-700">
                ✓ Integrity verified — bytes match recorded hash
              </span>
            )}
            {verifyResult && !verifyResult.ok && !verifyResult.error && (
              <span className="text-sm text-red-700">
                ✗ ALTERATION DETECTED — stored bytes do not match recorded
                hash
              </span>
            )}
            {verifyResult?.error && (
              <span className="text-sm text-red-700">
                {verifyResult.error}
              </span>
            )}
          </div>
          {verifyResult && !verifyResult.ok && verifyResult.actualSha256 && (
            <div className="text-xs text-slate-500">
              Actual:{" "}
              <code className="break-all">{verifyResult.actualSha256}</code>
            </div>
          )}

          <div className="border-t border-slate-100 pt-3">
            <div className="text-sm">
              Final email:{" "}
              <strong
                className={
                  docData.finalEmailStatus === "sent"
                    ? "text-emerald-700"
                    : docData.finalEmailStatus === "failed"
                    ? "text-red-700"
                    : "text-slate-500"
                }
              >
                {docData.finalEmailStatus || "unknown (pre-logging)"}
              </strong>
              {docData.finalEmailError && (
                <>
                  {" "}
                  —{" "}
                  <span className="text-red-600">
                    {docData.finalEmailError}
                  </span>
                </>
              )}
            </div>
            <button
              className="btn-secondary mt-2 text-sm"
              onClick={async () => {
                try {
                  const fn = httpsCallable(functions, "retryFinalEmail");
                  const { data } = await fn({ docId });
                  alert(`Re-sent to ${data.recipients} recipient(s).`);
                } catch (err) {
                  alert(err?.message || "Re-send failed");
                }
              }}
            >
              Re-send completion email
            </button>
          </div>
        </section>
      )}
    </div>
  );
}
